/**
 * /completar-registro — ver `MEMBERSHIP_ROUTE.COMPLETAR_REGISTRO`
 *
 * Formulario post-registro: unsafeMetadata. Redirige a `MEMBERSHIP_ROUTE.PENDING_APPROVAL`.
 * Parámetros de URL se propagan (sesión Stripe, MP, plan).
 * Guía pública: `MEMBERSHIP_ACCESS_PORTAL_PATH`.
 */

import { MembershipPortalHint } from "@/components/membership/MembershipPortalHint";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSEO } from "@/hooks/useSEO";
import {
  getRegistrationData,
  isClerkAdmin,
  isProfileComplete,
  MIN_REGISTRATION_AGE,
} from "@/lib/auth";
import {
  COMPLETAR_REGISTRO_FUNNEL_COPY,
  validateRegistrationDataEn,
} from "@/lib/membershipFunnelEnglish";
import { useMembershipFunnelEnglish } from "@/hooks/useMembershipFunnelEnglish";
import { BRAND_PERSON_NAME, BRAND_SITE_URL , BRAND_OG_IMAGE_URL } from "@/lib/brandConfig";
import { MEMBERSHIP_ROUTE } from "@/lib/membershipAccessFlow";
import { capturePostPurchaseProfileCompletedPostHog } from "@/lib/posthogAnalytics";
import { startVipMembershipVideoCheckout } from "@/lib/vipMembershipVideoCheckout";
import { detectCountryCode } from "@/lib/countryDetection";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";
import { RedirectToSignIn, useAuth, useUser, useSession } from "@clerk/clerk-react";
import { Clock, Loader2, Sparkles } from "lucide-react";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";

const copy = COMPLETAR_REGISTRO_FUNNEL_COPY;

function toIsoDate(value: string): string {
  return new Date(`${value}T00:00:00.000Z`).toISOString();
}

function getMaxBirthDate(): string {
  const now = new Date();
  const max = new Date(now.getFullYear() - MIN_REGISTRATION_AGE, now.getMonth(), now.getDate());
  return max.toISOString().slice(0, 10);
}

function buildPendingTarget(sessionId: string | null, sp: URLSearchParams): string {
  const planTier = sp.get("plan_tier") ?? "";
  const fromVip = sp.get("from_special_offer") === "1" || sp.get("from_vip") === "1" || planTier === "vip";

  // VIP: unified waiting room — same page as MercadoPago VIP
  if (fromVip && sessionId) {
    const q = new URLSearchParams();
    q.set("session_id", sessionId);
    q.set("plan", "vip");
    q.set("provider", "stripe");
    return `/muchas-gracias?${q.toString()}`;
  }

  const q = new URLSearchParams();
  if (sessionId) q.set("session_id", sessionId);
  const mpSub = sp.get("subscription_id");
  if (mpSub) q.set("subscription_id", mpSub);
  if (planTier) q.set("plan_tier", planTier);
  if (sp.get("from_basic") === "1") q.set("from_basic", "1");
  if (sp.get("from_standard") === "1") q.set("from_standard", "1");
  if (fromVip) q.set("from_vip", "1");
  if (sp.get("access_request") === "1") q.set("access_request", "1");
  const qs = q.toString();
  return qs ? `${MEMBERSHIP_ROUTE.PORTAL}?${qs}` : MEMBERSHIP_ROUTE.PORTAL;
}

export default function CompleteRegistration() {
  useMembershipFunnelEnglish();
  useSEO({
    title: copy.seoTitle,
    description: copy.seoDescription(BRAND_PERSON_NAME),
    keywords: "",
    ogImage: BRAND_OG_IMAGE_URL,
    canonical: `${BRAND_SITE_URL}${MEMBERSHIP_ROUTE.COMPLETAR_REGISTRO}`,
    robots: "noindex, nofollow" as const,
  });
  const navigate = useNavigate();
  const { isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  const [params] = useSearchParams();

  const { session } = useSession();

  const [alias, setAlias] = useState("");
  const [telegram, setTelegram] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Guard contra doble disparo del checkout VIP (StrictMode dev double-effect
  // o re-render mientras window.location.assign aún no navegó). Un disparo
  // duplicado crea dos Stripe Checkout Sessions abandonadas — molesto en
  // métricas pero no visible al usuario.
  const [vipCheckoutFiring, setVipCheckoutFiring] = useState(false);

  const maxBirthDate = useMemo(() => getMaxBirthDate(), []);

  const fromVip = params.get("from_special_offer") === "1" || params.get("from_vip") === "1";
  const planTier = (params.get("plan_tier") || "").trim().toLowerCase();
  const fromBasic = params.get("from_basic") === "1" || planTier === "basic";
  const fromStandard =
    params.get("from_standard") === "1" ||
    (planTier === "standard" && !fromBasic && !fromVip);
  const sessionId = params.get("session_id");
  const mercadopagoSubscriptionId = params.get("subscription_id");
  /**
   * return_to — landing path al que volver tras completar profile. Pasado por
   * MuxVideoPlayerWithCTA cuando el visitor fue gateado pre-checkout: tras
   * completar el alta lo regresamos a la URL original para que reclique el CTA
   * y entre al Stripe checkout ya autenticado. Validamos paths locales para
   * evitar open-redirect.
   */
  const rawReturnTo = params.get("return_to")?.trim() ?? "";
  const returnTo =
    rawReturnTo.startsWith("/") && !rawReturnTo.startsWith("//")
      ? rawReturnTo
      : "";
  const pendingTarget = buildPendingTarget(sessionId, params);

  // Pre-fill desde unsafeMetadata si ya tiene algo
  useEffect(() => {
    if (!user) return;
    const data = getRegistrationData(user);
    if (!alias && data.alias) setAlias(data.alias);
    if (!telegram && data.telegram) setTelegram(data.telegram);
    if (!birthDate && data.birthDate) {
      const parsed = new Date(data.birthDate);
      if (!Number.isNaN(parsed.getTime())) {
        setBirthDate(parsed.toISOString().slice(0, 10));
      }
    }
  }, [alias, telegram, birthDate, user]);

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (!isSignedIn) {
    return <RedirectToSignIn />;
  }

  // Si ya tiene perfil completo, ir a pending-approval.
  // Para free join: llamar a la API antes de redirigir (el usuario puede llegar aquí
  // con perfil completo si inicia sesión con una cuenta existente en vez de registrarse).
  // Para VIP pre-pago: si llega acá con perfil pero sin session_id, kickear el
  // Stripe/MP checkout con el Clerk email y mostrar loader.
  const variantIdParam = params.get("variant_id") ?? "";

  // Helper: dispara checkout VIP con email Clerk; post-pago → /portal/complete-profile (API).
  // Idempotente vía `vipCheckoutFiring` para evitar doble fire en StrictMode.
  const fireVipCheckoutPreAuth = () => {
    if (vipCheckoutFiring) return;
    setVipCheckoutFiring(true);
    const buyerEmail = user?.primaryEmailAddress?.emailAddress ?? "";
    const countryCode =
      getServerTemplateConfig()?.country ?? detectCountryCode() ?? "US";
    void startVipMembershipVideoCheckout({
      countryCode,
      pathname: window.location.pathname,
      languageForPlans: countryCode === "AR" ? "es-AR" : "en-US",
      email: buyerEmail,

    });
  };

  if (user && isProfileComplete(user)) {
    if (isClerkAdmin(user)) return <Navigate to="/admin" replace />;
    if (planTier === "free" && variantIdParam) {
      // Fire-and-forget: el portal polling se encargará del estado final
      void (async () => {
        try {
          const token = await session?.getToken();
          await fetch("/api/free-join", {
            method: "POST",
            headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify({ variant_id: variantIdParam }),
          });
        } catch { /* non-fatal */ }
      })();
      return <Navigate to="/portal?from_free=true" replace />;
    }
    // VIP pre-payment: usuario llega ya con perfil completo (sign-in con cuenta
    // existente desde el CTA VIP). Kickear el checkout — no hay un session_id
    // todavía porque el pago aún no ocurrió.
    if (planTier === "vip" && !sessionId) {
      fireVipCheckoutPreAuth();
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-zinc-950 text-zinc-300">
          <Loader2 className="w-8 h-8 animate-spin text-amber-300" />
          <p className="text-sm">{copy.openingCheckout}</p>
        </div>
      );
    }
    // return_to: visitor fue gateado por MuxVideoPlayerWithCTA (pre-checkout
    // Stripe) y ya tiene perfil → volver al landing para que reclique el CTA
    // y entre a Stripe ya autenticado.
    if (returnTo) {
      return <Navigate to={returnTo} replace />;
    }
    return <Navigate to={pendingTarget} replace />;
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user) return;
    const trimmedAlias = alias.trim();
    const trimmedTelegram = telegram.trim();
    const validationError = validateRegistrationDataEn({
      alias: trimmedAlias,
      birthDate,
      telegram: trimmedTelegram,
    });
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const existingMeta = (user.unsafeMetadata ?? {}) as Record<string, unknown>;
      await user.update({
        unsafeMetadata: {
          ...existingMeta,
          alias: trimmedAlias,
          birthDate: toIsoDate(birthDate),
          telegram: trimmedTelegram,
          ...(fromVip ? { from_vip_signup: true } : {}),
          ...(fromBasic ? { from_basic_signup: true } : {}),
          ...(fromStandard ? { from_standard_signup: true } : {}),
          ...(planTier ? { purchase_plan_tier: planTier } : {}),
          ...(sessionId ? { stripe_session_id: sessionId } : {}),
          ...(mercadopagoSubscriptionId
            ? { mercadopago_subscription_id: mercadopagoSubscriptionId }
            : {}),
        },
      });

      capturePostPurchaseProfileCompletedPostHog({
        plan_tier: planTier || null,
        from_vip: fromVip,
        from_basic: fromBasic,
        from_standard: fromStandard,
        stripe_session_id: sessionId,
        mercadopago_subscription_id: mercadopagoSubscriptionId,
      });

      if (isClerkAdmin(user)) {
        navigate("/admin", { replace: true });
        return;
      }

      // Free join: call API to create pending membership, then go to portal
      if (planTier === "free" && variantIdParam) {
        try {
          const token = await session?.getToken();
          await fetch("/api/free-join", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ variant_id: variantIdParam }),
          });
        } catch {
          // Non-fatal: portal polling will catch up
        }
        navigate("/portal?from_free=true", { replace: true });
        return;
      }

      // return_to: visitor fue gateado por MuxVideoPlayerWithCTA pre-checkout.
      // Tras guardar profile, lo regresamos al landing para que reclique el CTA
      // y entre a Stripe ya autenticado. NO va a Stripe inline acá porque el
      // pricing/pack se resuelve en el contexto de la landing (country, service,
      // overridePackId), no del registro.
      if (returnTo) {
        navigate(returnTo, { replace: true });
        return;
      }

      // VIP pre-payment: profile guardado → kick Stripe/MP checkout con Clerk
      // email + post-pago /portal/complete-profile. El webhook crea el row
      // user_memberships con status='pending_approval' (require_approval=true
      // ya está hardcodeado para vip en api/webhooks/stripe.ts:1045-1063).
      if (planTier === "vip" && !sessionId) {
        const buyerEmail = user.primaryEmailAddress?.emailAddress ?? "";
        const countryCode =
          getServerTemplateConfig()?.country ?? detectCountryCode() ?? "US";
        await startVipMembershipVideoCheckout({
          countryCode,
          pathname: window.location.pathname,
          languageForPlans: countryCode === "AR" ? "es-AR" : "en-US",
          email: buyerEmail,
    
        });
        // startVipMembershipVideoCheckout llama window.location.assign internamente.
        // Si por alguna razón no navegó (fan_fallback en error path), dejamos
        // saving=true para que el usuario no clickee de nuevo.
        return;
      }

      // Tras completar perfil → pending (equipo asigna link de ingreso)
      navigate(pendingTarget, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : copy.saveError);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 px-4 py-8 flex items-center justify-center">
      <div className="mx-auto max-w-md w-full">
        <Card className="border-white/10 bg-zinc-900/80 backdrop-blur shadow-2xl shadow-black/40">
          <CardHeader className="text-center space-y-3 pb-4">
            <div className="mx-auto w-14 h-14 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Sparkles className="w-7 h-7 text-amber-300" />
            </div>
            <CardTitle className="text-xl sm:text-2xl text-zinc-100">{copy.title}</CardTitle>
            <CardDescription className="text-sm text-zinc-400">
              {planTier === "free"
                ? copy.descFree
                : fromBasic || fromStandard
                  ? copy.descBasicStandard
                  : copy.descDefault}
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-2">
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="space-y-1.5">
                <Label htmlFor="alias" className="text-zinc-200">{copy.aliasLabel}</Label>
                <Input
                  id="alias"
                  value={alias}
                  onChange={(e) => {
                    setAlias(e.target.value);
                    if (error) setError(null);
                  }}
                  placeholder={copy.aliasPlaceholder}
                  minLength={3}
                  maxLength={30}
                  required
                  className="bg-black/40 border-white/10 text-zinc-100 placeholder:text-zinc-500 h-11"
                />
                <p className="text-xs text-zinc-500">{copy.aliasHint}</p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="telegram" className="text-zinc-200">{copy.telegramLabel}</Label>
                <Input
                  id="telegram"
                  value={telegram}
                  onChange={(e) => {
                    setTelegram(e.target.value);
                    if (error) setError(null);
                  }}
                  placeholder={copy.telegramPlaceholder}
                  minLength={3}
                  maxLength={50}
                  required
                  className="bg-black/40 border-white/10 text-zinc-100 placeholder:text-zinc-500 h-11"
                />
                <p className="text-xs text-zinc-500">{copy.telegramHint}</p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="birthDate" className="text-zinc-200">{copy.birthDateLabel}</Label>
                <Input
                  id="birthDate"
                  type="date"
                  value={birthDate}
                  max={maxBirthDate}
                  onChange={(e) => {
                    setBirthDate(e.target.value);
                    if (error) setError(null);
                  }}
                  required
                  className="bg-black/40 border-white/10 text-zinc-100 h-11"
                />
                <p className="text-xs text-zinc-500">{copy.birthDateHint(MIN_REGISTRATION_AGE)}</p>
              </div>

              {error && (
                <div className="rounded-lg bg-rose-500/10 border border-rose-500/30 px-3 py-2 text-xs text-rose-200">
                  {error}
                </div>
              )}

              {(planTier === "free" || fromVip || fromBasic || fromStandard) && (
                <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-xs text-amber-100 flex items-start gap-2">
                  <Clock className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                  <span>
                    {planTier === "free"
                      ? copy.noticeFree
                      : fromVip
                        ? copy.noticeVip
                        : copy.noticePaid}
                  </span>
                </div>
              )}

              <Button
                type="submit"
                disabled={saving}
                className="w-full h-11 bg-white text-black font-semibold hover:bg-white/90 gap-2"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {copy.saving}
                  </>
                ) : (
                  copy.saveButton
                )}
              </Button>

              <MembershipPortalHint className="pt-1" />
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
